import type { PropsWithChildren, ComponentProps } from 'react'
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import type { PhysicalHorizontalDirection, ScrollPosition } from 'LuxKit/Carousel/utils'
import {
  getLogicalScrollDeltaForPhysicalHorizontalDirection,
  getLogicalScrollLeft,
  isPhysicalHorizontalArrowHidden,
  scrollToLogicalPosition,
} from 'LuxKit/Carousel/utils'
import useTextDirection from 'hooks/useTextDirection'
import type Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import clsx from 'clsx'
export const MIN_MONTH_TAB_BUTTON_WIDTH: number = 108
const SCROLL_ADVANCE = MIN_MONTH_TAB_BUTTON_WIDTH * 3

const Wrapper = styled.div`
  width: 100%;
  position: relative;

  &.fit-content {
    width: fit-content;
    max-width: 100%;
  }
`

const Container = styled(HorizontalContainerWithFadedOverflow)`
  gap: ${rem(8)};
  align-items: stretch;
`

const FloatingActionContainer = styled.div`
  display: none;
  position: absolute;
  top: 50%;
  pointer-events: none;
  opacity: 1;
  transition: .2s ease opacity;

  ${mediaQueryUp.tablet} {
    display: block;
  }

  > * {
    pointer-events: initial;
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }

  &.at-start {
    inset-inline-start: 0;
    transform: translateX(-50%) translateY(-50%);

    [dir='rtl'] & {
      transform: translateX(50%) translateY(-50%);
    }
  }

  &.at-end {
    inset-inline-end: 0;
    transform: translateX(50%) translateY(-50%);

    [dir='rtl'] & {
      transform: translateX(-50%) translateY(-50%);
    }
  }

  &.is-hidden {
    opacity: 0;

    > * {
      pointer-events: none;
    }
  }
`

interface Props {
  className?: string
  fitContent?: boolean
  gap?: ComponentProps<typeof Group>['gap']
}

const MonthTabButtonsGroup = forwardRef<HTMLDivElement, PropsWithChildren<Props>>((props, ref) => {
  const {
    children,
    className,
    fitContent = false,
    gap,
  } = props

  const tabsGroupRef = useRef<HTMLDivElement>(null)
  const [hasReachedStart, setHasReachedStart] = useState<boolean>(true)
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(true)
  const textDirection = useTextDirection(tabsGroupRef)

  useImperativeHandle(ref, () => tabsGroupRef.current!)

  const scrollInPhysicalDirection = useCallback((direction: PhysicalHorizontalDirection) => {
    const tabsGroup = tabsGroupRef.current
    if (!tabsGroup) return
    const currentLeft = getLogicalScrollLeft(tabsGroup)
    const nextLeft = currentLeft + getLogicalScrollDeltaForPhysicalHorizontalDirection(
      textDirection,
      direction,
      SCROLL_ADVANCE + (currentLeft % SCROLL_ADVANCE),
    )

    scrollToLogicalPosition(tabsGroup, { left: nextLeft })
  }, [textDirection])

  const scrollBackward = useCallback(() => { scrollInPhysicalDirection('left') }, [scrollInPhysicalDirection])

  const scrollForward = useCallback(() => { scrollInPhysicalDirection('right') }, [scrollInPhysicalDirection])

  const scrollPosition: ScrollPosition = hasReachedStart ? 'start' : hasReachedEnd ? 'end' : 'middle'

  return <Wrapper className={clsx(className, { 'fit-content': fitContent })}>
    <Container
      ref={tabsGroupRef}
      onHasReachedStart={setHasReachedStart}
      onHasReachedEnd={setHasReachedEnd}
      gap={gap}
    >
      {children}
    </Container>
    <FloatingActionContainer
      className={clsx('at-start', {
        'is-hidden': isPhysicalHorizontalArrowHidden(scrollPosition, textDirection, 'left'),
      })}
    >
      <IconButton
        kind="primary"
        variant="ghost"
        size="small"
        shape="circle"
        onClick={scrollBackward}
      >
        <LineAngleLeftIcon rtlFlip={false} />
      </IconButton>
    </FloatingActionContainer>
    <FloatingActionContainer
      className={clsx('at-end', {
        'is-hidden': isPhysicalHorizontalArrowHidden(scrollPosition, textDirection, 'right'),
      })}
    >
      <IconButton
        kind="primary"
        size="small"
        variant="ghost"
        shape="circle"
        onClick={scrollForward}
      >
        <LineAngleRightIcon rtlFlip={false} />
      </IconButton>
    </FloatingActionContainer>
  </Wrapper>
})

export default MonthTabButtonsGroup
